import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { ModernLogo } from "@/components/ModernLogo";
import { 
  Shield, 
  CheckCircle, 
  AlertTriangle,
  Users,
  Lock,
  DollarSign,
  FileText,
  Star,
  TrendingUp,
  BadgeCheck,
  Ban,
  RefreshCw,
  CreditCard,
  MessageSquare,
  ArrowLeft
} from "lucide-react";
import { Link } from "react-router-dom";
import { SEO } from "@/components/SEO";

export function WhyUs() {
  const customerBenefits = [
    {
      icon: <Shield className="w-8 h-8 text-primary" />,
      title: "Full Deposit Protection",
      description: "7% deposit refunded if your tradesperson doesn't deliver quality work or abandons the job. Kept by platform only when job completes successfully and you're satisfied."
    },
    {
      icon: <BadgeCheck className="w-8 h-8 text-primary" />,
      title: "Verified Tradespeople Only",
      description: "Every tradesperson is insurance-verified and background-checked. No anonymous contractors or fake profiles."
    },
    {
      icon: <FileText className="w-8 h-8 text-primary" />,
      title: "Written Quotes & Agreements",
      description: "Everything documented on the platform. Legal protection with clear terms, dates, and costs agreed upfront."
    },
    {
      icon: <MessageSquare className="w-8 h-8 text-primary" />,
      title: "Monitored Communication",
      description: "All messages tracked and monitored for your safety. Report issues anytime with full chat history as evidence."
    },
    {
      icon: <Star className="w-8 h-8 text-primary" />,
      title: "Quality Guarantee",
      description: "Rate your tradesperson after completion. Poor ratings mean accountability and affect their ability to get future work."
    },
    {
      icon: <RefreshCw className="w-8 h-8 text-primary" />,
      title: "Dispute Resolution",
      description: "Fair mediation service if things go wrong. We review all evidence and ensure justice for both parties."
    }
  ];

  const tradesmanBenefits = [
    {
      icon: <DollarSign className="w-8 h-8 text-primary" />,
      title: "Payment Guaranteed",
      description: "Customer pays upfront before you start. No chasing payments, no excuses - your money is secured before you lift a finger."
    },
    {
      icon: <Shield className="w-8 h-8 text-primary" />,
      title: "Legal Protection",
      description: "Written agreements protect you from scope creep and unreasonable demands. Clear terms = clear boundaries."
    },
    {
      icon: <BadgeCheck className="w-8 h-8 text-primary" />,
      title: "Build Verified Reputation",
      description: "Every 5-star review is verified and permanent. Build trust that follows you across all jobs on the platform."
    },
    {
      icon: <Users className="w-8 h-8 text-primary" />,
      title: "Vetted Customers",
      description: "We verify customer identities and track their payment history. No more time-wasters or non-payers."
    },
    {
      icon: <MessageSquare className="w-8 h-8 text-primary" />,
      title: "Protected Communication",
      description: "All conversations logged. If a customer makes false claims, you have documented proof of what was agreed."
    },
    {
      icon: <TrendingUp className="w-8 h-8 text-primary" />,
      title: "Only 7% Commission",
      description: "Lowest in the industry. No monthly fees, no setup costs. Pay only when you earn, and keep 93% of every job."
    }
  ];

  const offPlatformRisks = [
    {
      risk: "For Customers",
      icon: <AlertTriangle className="w-6 h-6 text-destructive" />,
      consequences: [
        "Zero legal recourse if tradesperson disappears",
        "No refund protection for deposits paid",
        "No insurance verification - could be unqualified",
        "No proof of what was agreed verbally",
        "Liable if uninsured tradesperson gets injured on your property",
        "No quality standards or accountability"
      ]
    },
    {
      risk: "For Tradespeople",
      icon: <AlertTriangle className="w-6 h-6 text-destructive" />,
      consequences: [
        "Customers can refuse to pay with no consequence",
        "No legal protection for scope creep demands",
        "Waste time on customers who ghost you",
        "Cannot build verified reputation or reviews",
        "Risk of false damage claims without documentation",
        "No dispute resolution if things go wrong"
      ]
    }
  ];

  const ourGuarantees = [
    {
      title: "For Customers: Deposit Refund if Job Fails",
      description: "If your tradesperson doesn't show up, does poor quality work, or abandons your job, we refund your full 7% deposit - no questions asked. If you're satisfied with the completed work, the deposit is kept by the platform as part of our service fee.",
      icon: <Shield className="w-12 h-12 text-primary" />
    },
    {
      title: "For Tradespeople: Payment Before Work Guarantee",
      description: "You never start a job without secured payment. The customer pays upfront, and we hold it safely until job completion. No more chasing invoices or dealing with non-payers - you're guaranteed to get paid for your work.",
      icon: <CreditCard className="w-12 h-12 text-primary" />
    }
  ];

  return (
    <>
      <SEO 
        title="Why Choose Our Platform - Unmatched Protection & Guarantees"
        description="The only platform that protects both customers and tradespeople with verified insurance, payment guarantees, and full deposit refunds. See why staying on-platform is in everyone's interest."
        keywords="tradesperson platform benefits, deposit refund guarantee, payment protection, verified tradespeople, customer protection, tradesperson payment guarantee"
      />

      <div className="min-h-screen bg-background">
        {/* Header */}
        <header className="sticky top-0 z-50 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 border-b">
          <div className="container mx-auto px-4 py-4">
            <div className="flex items-center justify-between">
              <Link to="/" className="flex items-center gap-2">
                <ModernLogo className="h-8" />
              </Link>
              <Link to="/">
                <Button variant="ghost" size="sm">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back to Home
                </Button>
              </Link>
            </div>
          </div>
        </header>

        {/* Hero Section */}
        <section className="py-16 md:py-24 bg-gradient-to-b from-primary/5 to-background">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-2 rounded-full mb-6">
                <Shield className="w-5 h-5" />
                <span className="font-semibold">Protected by Industry-Leading Guarantees</span>
              </div>
              <h1 className="text-4xl md:text-6xl font-bold mb-6">
                Why Our Platform Is <span className="text-primary">Unbeatable</span>
              </h1>
              <p className="text-xl text-muted-foreground mb-8 max-w-3xl mx-auto">
                We're not just another listing site. We're the only platform that truly protects both customers and tradespeople with verified guarantees, legal safeguards, and fair pricing.
              </p>
            </div>
          </div>
        </section>

        {/* Our Guarantees */}
        <section className="py-16 bg-background">
          <div className="container mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold mb-4">Our Iron-Clad Guarantees</h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                These aren't just promises. They're contractual guarantees backed by our platform.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8 max-w-6xl mx-auto">
              {ourGuarantees.map((guarantee, index) => (
                <Card key={index} className="border-primary/20">
                  <CardHeader>
                    <div className="mb-4">{guarantee.icon}</div>
                    <CardTitle className="text-2xl">{guarantee.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground text-lg">{guarantee.description}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Customer Benefits */}
        <section className="py-16 bg-muted/30">
          <div className="container mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold mb-4">For Customers: Complete Peace of Mind</h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                Why risk losing money and time with unverified tradespeople off-platform?
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-7xl mx-auto">
              {customerBenefits.map((benefit, index) => (
                <Card key={index} className="hover:shadow-lg transition-shadow">
                  <CardHeader>
                    <div className="mb-3">{benefit.icon}</div>
                    <CardTitle className="text-xl">{benefit.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground">{benefit.description}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Tradesperson Benefits */}
        <section className="py-16 bg-background">
          <div className="container mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold mb-4">For Tradespeople: Get Paid, Stay Protected</h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                Stop wasting time on time-wasters. Only 7% commission, guaranteed payment, verified reputation.
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-7xl mx-auto">
              {tradesmanBenefits.map((benefit, index) => (
                <Card key={index} className="hover:shadow-lg transition-shadow">
                  <CardHeader>
                    <div className="mb-3">{benefit.icon}</div>
                    <CardTitle className="text-xl">{benefit.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground">{benefit.description}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Off-Platform Risks */}
        <section className="py-16 bg-destructive/5">
          <div className="container mx-auto px-4">
            <div className="text-center mb-12">
              <div className="inline-flex items-center gap-2 bg-destructive/10 text-destructive px-4 py-2 rounded-full mb-6">
                <Ban className="w-5 h-5" />
                <span className="font-semibold">Warning: Off-Platform Deals</span>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold mb-4">The Real Cost of Going Off-Platform</h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                Think you're saving money? Here's what you're actually risking.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8 max-w-6xl mx-auto">
              {offPlatformRisks.map((item, index) => (
                <Card key={index} className="border-destructive/20">
                  <CardHeader>
                    <div className="flex items-center gap-3 mb-3">
                      {item.icon}
                      <CardTitle className="text-2xl">{item.risk}</CardTitle>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-3">
                      {item.consequences.map((consequence, idx) => (
                        <li key={idx} className="flex items-start gap-2">
                          <AlertTriangle className="w-5 h-5 text-destructive mt-0.5 flex-shrink-0" />
                          <span className="text-muted-foreground">{consequence}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Pricing Transparency */}
        <section className="py-16 bg-background">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="text-3xl md:text-4xl font-bold mb-4">Our Pricing: Fair, Transparent, Lowest in Industry</h2>
                <p className="text-lg text-muted-foreground">
                  We only succeed when you succeed. That's why our fees are the lowest.
                </p>
              </div>

              <Card className="border-primary/20">
                <CardHeader>
                  <CardTitle className="text-2xl">How Payment Works</CardTitle>
                  <CardDescription className="text-base">
                    Protecting both parties while keeping costs minimal
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="space-y-4">
                    <div className="flex items-start gap-4 p-4 bg-muted/50 rounded-lg">
                      <CheckCircle className="w-6 h-6 text-primary mt-1 flex-shrink-0" />
                      <div>
                        <h3 className="font-semibold text-lg mb-1">3% Booking Fee (Customer)</h3>
                        <p className="text-muted-foreground">Small fee to access verified, insured tradespeople and full platform protection</p>
                      </div>
                    </div>

                    <div className="flex items-start gap-4 p-4 bg-muted/50 rounded-lg">
                      <CheckCircle className="w-6 h-6 text-primary mt-1 flex-shrink-0" />
                      <div>
                        <h3 className="font-semibold text-lg mb-1">7% Deposit (Customer) - Platform Fee</h3>
                        <p className="text-muted-foreground">Held as insurance until job completion. Kept by platform if you're satisfied with the completed work. Only refunded if job fails to complete or you're not satisfied with the results.</p>
                      </div>
                    </div>

                    <div className="flex items-start gap-4 p-4 bg-muted/50 rounded-lg">
                      <CheckCircle className="w-6 h-6 text-primary mt-1 flex-shrink-0" />
                      <div>
                        <h3 className="font-semibold text-lg mb-1">7% Commission (Tradesperson)</h3>
                        <p className="text-muted-foreground">Industry's lowest rate. No monthly fees, no setup costs. You keep 93% of every job.</p>
                      </div>
                    </div>
                  </div>

                  <div className="pt-6 border-t">
                    <div className="bg-primary/10 p-6 rounded-lg">
                      <h3 className="font-bold text-xl mb-3">Why This Benefits Everyone</h3>
                      <ul className="space-y-2 text-muted-foreground">
                        <li className="flex items-start gap-2">
                          <CheckCircle className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                          <span><strong>Customers:</strong> Pay slightly more but get guaranteed quality, full refund protection, and verified professionals</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <CheckCircle className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                          <span><strong>Tradespeople:</strong> Pay only 7% but get guaranteed payment, zero time-wasters, and premium leads</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <CheckCircle className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                          <span><strong>Both:</strong> Legal protection, documented agreements, dispute resolution, and verified ratings</span>
                        </li>
                      </ul>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* Final CTA */}
        <section className="py-16 bg-gradient-to-b from-primary/5 to-background">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <Shield className="w-16 h-16 text-primary mx-auto mb-6" />
              <h2 className="text-3xl md:text-4xl font-bold mb-4">
                Don't Risk It. Stay Protected.
              </h2>
              <p className="text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
                A few pounds in fees is worth thousands in protection. Whether you're hiring or working, our platform ensures you're covered, paid, and protected every step of the way.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/create-job">
                  <Button size="lg" className="text-lg px-8">
                    Post a Job (Customers)
                  </Button>
                </Link>
                <Link to="/tradesman-registration">
                  <Button size="lg" variant="outline" className="text-lg px-8">
                    Join as Tradesperson
                  </Button>
                </Link>
              </div>

              <div className="mt-12 pt-12 border-t">
                <p className="text-sm text-muted-foreground italic max-w-3xl mx-auto">
                  <Lock className="w-4 h-4 inline mr-1" />
                  <strong>Important:</strong> Jobs completed off-platform void all guarantees and protections. Keep all communication and payments on the platform to stay fully protected and maintain your verified reputation.
                </p>
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
}
